import React, { useState, useEffect } from 'react'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import { HistoryMobileDateRangePicker } from '../HistoryDateRangePickers'
import MobilePaginationActions from './MobilePaginationActions'

const formatRentalSummary = (rental) =>
    [rental?.ip, rental?.country, rental?.rentalid].filter(Boolean).join(' ')

const getTransactionMemo = (row) => {
    if (row?.memo) return row.memo
    if (row?.rental) {
        if (row.type === 'rental' || row.type === 'renewal' || row.type === 'refund') {
            return formatRentalSummary(row.rental)
        }
    }
    return ''
}


const TransactionChip = ({ type }) => (
    <span className="shop-type-chip">{type}</span>
)


const MobilePaymentsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [paymentsList, setPaymentsList] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs())
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'))
    const [showFilters, setShowFilters] = useState(false)


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - paymentsList?.balanceRecords?.length) : 0

    const handleChangePage = React.useCallback((event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }, [])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')
            
            // Получаем токен один раз
            const token = await getAccessTokenSilently()
            
            const paymentsPesponse = await props.apiClient.setToken(token).getUserPayments(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setPaymentsList(paymentsPesponse)
          } catch (e) {
          }
      }
     
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const getPaymentsDefaultRange = React.useCallback(() => ({
      start: dayjs().startOf('day'),
      end: dayjs().endOf('month')
    }), [])

    return (
        <div style={{
            width: '100%',
            height: '100%',
            padding: 'var(--spacing-sm)',
            overflow: 'auto'
        }}>
            
            {/* Мобильные фильтры по дате */}
            <div className="mobile-filters">
                <div 
                    className="mobile-filters-header"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <span className="mobile-filters-title">Date Filters</span>
                    <span style={{ 
                        color: 'var(--text-muted)',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {showFilters && (
                    <div className="mobile-filters-content">
                        <div className="mobile-filters-grid" style={{ gridTemplateColumns: '1fr' }}>
                            <HistoryMobileDateRangePicker
                                label="Date range"
                                startDate={startDate}
                                endDate={endDate}
                                onRangeChange={handleDateRangeChange}
                                getResetRange={getPaymentsDefaultRange}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {paymentsList?.balanceRecords?.length > 0 ? (
                <div>
                    {/* Мобильные карточки транзакций */}
                    <div style={{ padding: '0' }}>
                        {paymentsList?.balanceRecords?.map((row, index) => (
                            <div
                                key={index}
                                className="mobile-payment-card"
                            >
                                {/* Заголовок карточки */}
                                <div className="mobile-payment-header">
                                    <div>
                                        <div className={`mobile-payment-amount ${row?.amount < 0 ? 'amount-negative' : 'amount-positive'}`}>
                                            {row?.amount < 0 ? `-$${row?.amount ? (Math.abs(row.amount)/100).toFixed(2) : '0.00'}` : `+$${row?.amount ? (row.amount/100).toFixed(2) : '0.00'}`}
                                        </div>
                                        <div className="mobile-payment-date">
                                            {moment(row?.recordedAt).format('MMM DD, YYYY')}
                                        </div>
                                    </div>
                                    <TransactionChip type={row?.type} />
                                </div>

                                {/* Детали транзакции */}
                                <div className="mobile-payment-details">
                                    <div className="mobile-payment-detail">
                                        <div className="mobile-payment-detail-label">Description</div>
                                        <div className="mobile-payment-detail-value">
                                            {getTransactionMemo(row)?.replace(/(.{60})..+/, "$1…")}
                                        </div>
                                    </div>
                                    
                                    <div className="mobile-payment-detail">
                                        <div className="mobile-payment-detail-label">Balance</div>
                                        <div className="shop-accent-text mobile-payment-detail-value" style={{ fontWeight: '600' }}>
                                            ${row?.balance ? (row.balance/100).toFixed(2) : '0.00'}
                                        </div>
                                    </div>
                                </div>


                                {/* Время транзакции */}
                                <div className="mobile-payment-time">
                                    {moment(row?.recordedAt).format('h:mm A')}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Мобильная пагинация */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Строка с навигацией */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <MobilePaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={paymentsList?.total || 0}
                                onPageChange={handleChangePage}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">💰</div>
                    <h3 className="empty-state-title">No transactions found</h3>
                    <p className="empty-state-description">
                        You don't have any transaction records for the selected period.
                    </p>
                </div>
            )}
        </div>
    )
}

export default MobilePaymentsTable
